// Best-effort detection of "in-app browser" WebViews — the stripped-down
// embedded browser that messaging/social apps (Telegram, Viber, WhatsApp,
// Instagram, Facebook, TikTok, Line, WeChat, …) open when a player taps a
// link inside the app, instead of handing off to the device's real browser.
// These WebViews commonly break exactly what a WebGL slot game depends on:
// autoplay audio is blocked more aggressively than in a real browser,
// `requestFullscreen` is frequently unavailable, and some (older Android
// WebViews especially) cap or refuse WebGL contexts entirely.
//
// There's no single reliable cross-platform signal for this, so it
// combines two heuristics:
//
//   1. Known app UA tokens — most major apps append their own identifier
//      to the WebView's user-agent string (they need it for their own
//      analytics/deep-linking), so those can be named directly.
//   2. The generic Android WebView marker (`; wv)`) — Android's WebView
//      component injects this into its UA regardless of the hosting app,
//      so it catches apps that don't customize their UA at all (notably
//      Viber, and some Telegram builds, on Android).
//
// iOS has no equivalent generic marker: a WKWebView's UA is
// indistinguishable from Safari's unless the host app customizes it
// (which the apps in the token list below do). Telegram and Viber on iOS
// mostly hand external links to `SFSafariViewController` instead of a
// custom WebView, which is a dead end for this kind of detection: Apple
// sandboxes SFSafariViewController completely away from its hosting app —
// no custom UA, no injectable JS bridge, not even a distinguishing
// `document.referrer` — so there is no client-side signal at all that
// can tell "opened via Telegram" apart from "opened in real Safari" once
// the page is running. See `hasInAppBrowserParam` below for how that case
// is still handled.
const KNOWN_IN_APP_TOKENS: RegExp[] = [
  /FBAN|FBAV/i, // Facebook
  /Instagram/i,
  /\bLine\//i, // LINE
  /MicroMessenger/i, // WeChat
  /\bWhatsApp\b/i,
  /\bViber\b/i,
  /\bTelegram\b/i,
  /TikTok|musical_ly/i,
  /\bSnapchat\b/i,
  /\bPinterest\b/i,
  /LinkedInApp/i,
  /\bDiscord\b/i,
  /KAKAOTALK/i,
  /NAVER\(inapp/i,
  /GSA\//i, // Google app (iOS) in-app browser
  /\bTwitter\b/i,
];

const ANDROID_GENERIC_WEBVIEW = /; ?wv\)/i;

/**
 * True when `userAgent` looks like a messaging/social app's embedded
 * WebView rather than the device's real browser. Defaults to
 * `navigator.userAgent`; accepts an explicit string for testing.
 */
export function isInAppBrowser(
  userAgent: string = typeof navigator !== 'undefined' ? navigator.userAgent : '',
): boolean {
  if (!userAgent) return false;
  return (
    KNOWN_IN_APP_TOKENS.some((re) => re.test(userAgent)) ||
    ANDROID_GENERIC_WEBVIEW.test(userAgent)
  );
}

/**
 * Explicit override for the cases `isInAppBrowser` structurally can't
 * catch — chiefly Telegram/Viber on iOS (see the comment above): since
 * there's no signal left inside the page to detect those, the fix has to
 * happen before the page even loads. Whoever generates the outbound link
 * (a Telegram bot message, a Viber broadcast, a WhatsApp share, …) tags it
 * themselves with `?inAppBrowser=1` (or `?inAppBrowser=telegram` etc. for
 * a slightly more specific value — the value itself isn't inspected here,
 * only its presence) before sending it out, and this reads that tag back.
 * Defaults to `location.search`; accepts an explicit string for testing.
 */
export function hasInAppBrowserParam(
  search: string = typeof location !== 'undefined' ? location.search : '',
): boolean {
  if (!search) return false;
  return new URLSearchParams(search).has('inAppBrowser');
}
